import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable as PrimeReactDataTable } from 'primereact/datatable';
import { TabPanel, TabView } from 'primereact/tabview';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as ArrowDownIcon } from '../../assets/images/arrow_down_white.svg';
import { ReactComponent as ArrowUpIcon } from '../../assets/images/arrow_up_white.svg';
import { ReactComponent as SelectAllIcon } from '../../assets/images/select-all-icon.svg';
import ConfirmDialog from '../../component/confirmdialog/ConfirmDialog';
import { UsersAssignedToProfileDataTableContext, UsersAssignedToProfileDataTableProvider } from '../../component/datatable/DataTableContext';
import InputSwitch from '../../component/input/InputSwitch';
import InputText from '../../component/input/InputText';
import Checkbox from '../../component/input/Checkbox';
import LoadingSpinner from '../../component/loadingoverlay/LoadingSpinner';
import { LayoutContext } from '../../context/LayoutContext';
import { MessageContext } from '../../context/MessageContext';
import { UserRoleService } from '../../service/UserRoleService';
import { UserService } from '../../service/UserService';
import useNavigationContext from '../../utils/navigationcontext/NavigationContext';
import { RenderedHelper } from '../../utils/render/RenderedHelper';
import useValidator from '../../utils/validator/useValidator';
import './UserProfile.scss';
import DataTable, { FILTER_TYPES } from '../../component/datatable/DataTable';
import moment from 'moment';
import { Constants } from '../../utils/Constants';

const UserProfile = ({ viewMode }) => {
    return (        
        <UsersAssignedToProfileDataTableProvider>
            <UserProfileImpl viewMode={viewMode} />
        </UsersAssignedToProfileDataTableProvider>
    )
}

const UserProfileImpl = ({ viewMode }) => {
    const { setPageTitle, setShowPageTitle, setShowBackButton, setBackButtonLabel, setBackButtonNavigateTo, setActionButtons } = useContext(LayoutContext);
    const { showSuccessMessage, showErrorMessage } = useContext(MessageContext);
    const TABS = useMemo(() => ({
        GENERAL: 0,
        ASSIGNED_USERS: 1
    }), []);
    const [activeTab, setActiveTab] = useState(TABS.GENERAL);
    const { t } = useTranslation();
    const validator = useValidator();
    const [form, setForm] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isTemplateLoading, setIsTemplateLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [mainKey, setMainKey] = useState(0);
    const [isNameUnique, setIsNameUnique] = useState(true);
    const [expandedRows, setExpandedRows] = useState({});
    const [deleteProfileDialogState, setDeleteProfileDialogState] = useState({ visible: false, state: null });
    const [userStatusOptions, setUserStatusOptions] = useState([]);
    const [vendappAccess, setVendappAccess] = useState(false);
    const enums = useMemo(() => [
        {
            name: 'UserStatus',
            values: userStatusOptions.map(r => ({key: r.id, value: r.label}))
        }
    ], [userStatusOptions]);
    const [assignedServicesTaskTypes, setAssignedServicesTaskTypes] = useState(false);
    const [selectedTaskTypes, setSelectedTaskTypes] = useState({
        PRODUCT_RESTOCKING: false,
        CASH_OPERATION: false,
        REPLACEMENT: false,
        OPERATIONAL: false,
        INVENTORY_CHECK: false,
        TECHNICAL: false
    });
    const { searchCriteria: assignedUsersSearchCriteria, setSearchCriteria: setAssignedUsersSearchCriteria } = useContext(UsersAssignedToProfileDataTableContext);

    const navigate = useNavigate();
    const { profileId } = useParams();
    const isMounted = useRef(false);

    const defaultAssignedUsersSearchCriteria = useMemo(() => ({
        first: 0,
        rows: 10,
        sortField: 'name',
        sortOrder: 1,
        baseQuery: '',
        filters: {},
    }), []);

    const navigationContext = useNavigationContext((params) => {
        setAssignedUsersSearchCriteria(params?.assignedUsersSearchCriteria ? params.assignedUsersSearchCriteria : defaultAssignedUsersSearchCriteria);
        setActiveTab(params?.activeTab !== undefined ? params.activeTab : TABS.GENERAL);
    });

    useEffect(() => {
        navigationContext.putParam('assignedUsersSearchCriteria', assignedUsersSearchCriteria);
        navigationContext.putParam('activeTab', activeTab);
    }, [assignedUsersSearchCriteria, activeTab]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            setBackButtonLabel(null);
            setActionButtons(null);
        }
    }, []);

    useEffect(() => {
        setShowPageTitle(false);
        setShowBackButton(true);
        setBackButtonNavigateTo(`/users?${navigationContext.getContextParam()}`);
        if (viewMode === 'NEW') {
            fetchTemplate();
        } else if (viewMode === 'EDIT') {
            fetchUserStatusOptions();
            setIsNameUnique(true);
        }
    }, [viewMode]);

    useEffect(() => {
        if (viewMode === 'EDIT' && profileId) {
            fetchUserProfile();
        }
    }, [viewMode, profileId]);

    useEffect(() => {
        if (deleteProfileDialogState.state === 'validate') {
            const validateDeleteProfile = async () => {
                try {
                    const count = await UserRoleService.getAssignedUsersCount(profileId);
                    if (count > 0) {
                        // Nie można usunąć - pokaż błąd
                        showErrorMessage(t('users.profiles.cannotDeleteWithUsers', { count }));
                        setDeleteProfileDialogState({ visible: false, state: null });
                    } else {
                        // Można usunąć - pokaż dialog potwierdzenia
                        setDeleteProfileDialogState({ visible: true, state: 'confirm' });
                    }
                } catch (err) {
                    showErrorMessage(t('users.error.fetchAssignedToProfileUsersCount'));
                    setDeleteProfileDialogState({ visible: false, state: null });
                }
            };
            validateDeleteProfile();
        }
    }, [deleteProfileDialogState.state]);


    const backButtonLabel = useMemo(() => {
        if (viewMode === 'NEW') {
            setBackButtonLabel(t('userprofile.newProfile'));
        } else if (form?.name) {
            return (
                <div className="flex gap-3 align-items-center">
                    <div>
                        {form?.name}
                    </div>
                </div>
            )
        } else {
            setBackButtonLabel(() => <LoadingSpinner size="1rem" />);
        }
    }, [form?.name, viewMode]);

    useEffect(() => {
        if (form?.name) {
            setBackButtonLabel(() => backButtonLabel);
            setActionButtons(() => actionButtons);
        }
    }, [form?.name]);

    const actionButtons = useMemo(() => {
        if (viewMode === 'EDIT') {
            return (
                <>
                    {activeTab === 0 ? (<Button
                        label={t('userprofile.delete')}
                        disabled={false}
                        className='small delete-button'
                        severity="danger"
                        onClick={() => setDeleteProfileDialogState({ visible: false, state: 'validate' })}
                    />) : null}
                </>
            )
        } else {
            return null;
        }
    }, [form.name, viewMode]);

    useEffect(() => {
        if (activeTab === TABS.GENERAL) {
            setActionButtons(() => actionButtons);
        } else {
            setActionButtons(null);
        }
    }, [activeTab]);

    const forceUpdate = () => {
        setMainKey(prev => prev + 1);
    }

    validator.addCustomRule('name_unique', t('validator.user_profile_name_unique'),
        (value) => {
            return isNameUnique;
        }
    )

    useEffect(() => {
        if (form.name) {
            if (!validator.isFieldValid('text-name')) {
                validator.showMessage('text-name');
                forceUpdate();
            }
        }
    }, [isNameUnique]);

    const fetchUserStatusOptions = () => {
        UserService.getStatusList().then(statuses => {
            setUserStatusOptions(statuses.map(r => ({ id: r, label: t(`UserStatus.${r}`) })));
        }).catch(err => {
            showErrorMessage(t('users.error.fetchUserStatuses'));
        });
    }

    // Aktualizuj userStatusOptions gdy zmieni się język
    useEffect(() => {
        if (userStatusOptions.length > 0) {
            setUserStatusOptions(prevStatusOptions => 
                prevStatusOptions.map(r => ({ id: r.id, label: t(`UserStatus.${r.id}`) }))
            );
        }
    }, [i18n.language]);

    const fetchUserProfile = () => {
        setIsLoading(true);
        UserRoleService.get(profileId).then(userProfile => {
            setForm(userProfile);
            if (userProfile.mobileAppAccess !== undefined) {
                setVendappAccess(userProfile.mobileAppAccess);
            }
            // Ustaw wartości typów zadań serwisowych
            setSelectedTaskTypes({
                PRODUCT_RESTOCKING: userProfile.restockProduct || false,
                CASH_OPERATION: userProfile.cashOperations || false,
                REPLACEMENT: userProfile.replaceProduct || false,
                OPERATIONAL: userProfile.operationalTasks || false,
                INVENTORY_CHECK: userProfile.inventoryCheck || false,
                TECHNICAL: userProfile.technicalTasks || false
            });
            // Ustaw przełącznik "Assigned services task types" jeśli którykolwiek typ jest włączony
            const hasAnyTaskType = userProfile.restockProduct || userProfile.replaceProduct || 
                                  userProfile.inventoryCheck || userProfile.cashOperations || 
                                  userProfile.operationalTasks || userProfile.technicalTasks;
            setAssignedServicesTaskTypes(hasAnyTaskType);
        }).catch(err => {
            showErrorMessage(t('userprofile.error.fetchUserProfile'));
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const fetchAssigedUsersData = async (criteria) => {
        criteria.userRoleId = profileId;
        try {
            return await UserService.getList(criteria, enums);
        } catch (err) {
            console.log('UserService::fetch err', err);
            showErrorMessage(t('users.error.fetch'));
        }
    };

    const fetchTemplate = () => {
        setIsTemplateLoading(true);
        UserRoleService.getTemplate().then(template => {
            setForm(template);
            if (template.mobileAppAccess !== undefined) {
                setVendappAccess(template.mobileAppAccess);
            }
        }).catch(err => {
            showErrorMessage(t('userprofile.error.fetchTemplate'));
        }).finally(() => {
            setIsTemplateLoading(false);
        });
    }

    const deleteProfile = async () => {
        try {
            await UserRoleService.delete(profileId);
            showSuccessMessage(t('users.deleteProfileDialog.successMessage'));
            navigate(`/users?${navigationContext.getContextParam()}`);
        } catch (error) {
            showErrorMessage(t('users.deleteProfileDialog.errorMessage'));
        }
        setDeleteProfileDialogState({ visible: false, state: null });
    }

    const revisionTemplate = (row) => {
        const isDisabled = !form.portalAccess;
        return (
            <InputSwitch
                checked={row?.read || false}
                disabled={isDisabled}
                onChange={e => {
                    if (!isDisabled) {
                        updateGroupPermission(row.code, 'read', e.value);
                    }
                }}
            />
        );
    }

    const handleExpanderClick = (row, e) => {
        setExpandedRows(prev => {
            if (prev[row.code]) {
                const { [row.code]: removed, ...rest } = prev;
                return rest;
            } else {
                return { ...prev, [row.code]: true };
            }
        });
    }

    const editingTemplate = (row) => {
        const hasChildren = row?.tabs?.length > 0;
        const isExpanded = expandedRows[row.code];
        const isDisabled = !form.portalAccess;
        return (
            <div className='flex'>
                <InputSwitch
                    checked={row?.write || false}
                    disabled={isDisabled}
                    onChange={e => {
                        if (!isDisabled) {
                            updateGroupPermission(row.code, 'write', e.value);
                        }
                    }}
                />
                {hasChildren && <div
                    className={`expander${isDisabled ? ' disabled' : ''}`}
                    onClick={(e) => {
                        if (!isDisabled) {
                            handleExpanderClick(row, e);
                        }
                    }}
                >
                    {isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
                </div>}
            </div>
        );
    }

    // Funkcja do aktualizacji uprawnień na poziomie grupy (read/write)
    const updateGroupPermission = (groupCode, permissionType, newValue) => {
        setForm(f => ({
            ...f,
            groups: f.groups?.map(g => {
                if (g.code === groupCode) {
                    const updatedGroup = { ...g, [permissionType]: newValue };

                    // Jeśli zmieniamy READ permission i mamy taby
                    if (permissionType === 'read' && g.tabs?.length > 0) {
                        if (newValue) {
                            // Włączamy READ - włączamy wszystkie taby
                            updatedGroup.tabs = setAllChildrenToTrue(g.tabs);
                        } else {
                            // Wyłączamy READ - wyłączamy wszystkie taby
                            updatedGroup.tabs = setAllChildrenToFalse(g.tabs);
                        }
                    }

                    return updatedGroup;
                }
                return g;
            }) || []
        }));
    };

    // Funkcja rekurencyjna do ustawiania wszystkich dzieci na false
    const setAllChildrenToFalse = (tabs) => {
        return tabs.map(tab => ({
            ...tab,
            value: false,
            tabs: tab.tabs?.length > 0 ? setAllChildrenToFalse(tab.tabs) : []
        }));
    };

    // Funkcja rekurencyjna do ustawiania wszystkich dzieci na true
    const setAllChildrenToTrue = (tabs) => {
        return tabs.map(tab => ({
            ...tab,
            value: true,
            tabs: tab.tabs?.length > 0 ? setAllChildrenToTrue(tab.tabs) : []
        }));
    };

    // Funkcja rekurencyjna do aktualizacji zagnieżdżonych tabów
    const updateNestedTab = (tabs, targetCode, newValue) => {
        return tabs.map(tab => {
            if (tab.code === targetCode) {
                const updatedTab = { ...tab, value: newValue };

                // Jeśli mamy dzieci, aktualizujemy je zgodnie z wartością rodzica
                if (tab.tabs?.length > 0) {
                    if (newValue) {
                        // Włączamy rodzica - włączamy wszystkie dzieci
                        updatedTab.tabs = setAllChildrenToTrue(tab.tabs);
                    } else {
                        // Wyłączamy rodzica - wyłączamy wszystkie dzieci
                        updatedTab.tabs = setAllChildrenToFalse(tab.tabs);
                    }
                }

                return updatedTab;
            } else if (tab.tabs && tab.tabs.length > 0) {
                return {
                    ...tab,
                    tabs: updateNestedTab(tab.tabs, targetCode, newValue)
                };
            }
            return tab;
        });
    };

    const handleTabPermissionChange = (groupCode, tabCode, newValue) => {
        setForm(f => ({
            ...f,
            groups: f.groups?.map(g =>
                g.code === groupCode
                    ? {
                        ...g,
                        tabs: updateNestedTab(g.tabs, tabCode, newValue)
                    }
                    : g
            ) || []
        }));
    };

    // Funkcja już istnieje wyżej jako setAllChildrenToTrue

    const handleSelectAll = () => {
        setForm(f => ({
            ...f,
            groups: f.groups?.map(g => ({
                ...g,
                read: true,
                write: true,
                tabs: g.tabs?.length > 0 ? setAllChildrenToTrue(g.tabs) : []
            })) || []
        }));
    }

    const handleSave = () => {
        if (validator.allValid()) {
            setIsSaving(true);
            const formData = {
                ...form,
                mobileAppAccess: vendappAccess,
                restockProduct: selectedTaskTypes.PRODUCT_RESTOCKING,
                replaceProduct: selectedTaskTypes.REPLACEMENT,
                inventoryCheck: selectedTaskTypes.INVENTORY_CHECK,
                cashOperations: selectedTaskTypes.CASH_OPERATION,
                operationalTasks: selectedTaskTypes.OPERATIONAL,
                technicalTasks: selectedTaskTypes.TECHNICAL
            };
            if (viewMode === 'NEW') {
                UserRoleService.create(formData).then(() => {
                    setIsSaving(false);
                    showSuccessMessage(t('userprofile.saved'));
                    navigate(`/users?${navigationContext.getContextParam()}`);
                }).catch(err => {
                    setIsSaving(false);
                    showErrorMessage(t('userprofile.error.save'));
                });
            } else {
                UserRoleService.update(formData).then(() => {
                    setIsSaving(false);
                    showSuccessMessage(t('userprofile.saved'));
                    navigate(`/users?${navigationContext.getContextParam()}`);
                }).catch(err => {
                    setIsSaving(false);
                    if (err?.err?.errorCode === 'EMAIL_REQUIRED') {
                        showErrorMessage(t('userprofile.error.emailRequired'));
                    } else if (err?.err?.errorCode === 'PHONE_NUMBER_REQUIRED') {
                        showErrorMessage(t('userprofile.error.phoneRequired'));
                    } else {
                        showErrorMessage(t('userprofile.error.save'));
                    }
                });
            }
        } else {
            validator.showMessages();
            forceUpdate();
        }
    }
    const handleCheckNameStatus = () => {
        UserRoleService.getNameStatus(form.name, form.id).then(response => {
            setIsNameUnique(!response.exists);
        }).catch(err => {
            showErrorMessage(t('userprofile.error.nameStatus'));
        })
    }

    const apiValueFromConverter = useMemo(() =>
        value => moment(value).startOf('day').utc().format(Constants.DATE_TIME_FORMAT_GET),
        []
    );
    const apiValueToConverter = useMemo(() =>
        value => moment(value).endOf('day').utc().format(Constants.DATE_TIME_FORMAT_GET),
        []
    );

    const assignedUsersFilterConfig = useMemo(() => [
        {
            fieldName: 'name',
            label: t('users.userName'),
            type: FILTER_TYPES.TEXT,
            apiParamName: 'username'
        },
        {
            fieldName: 'email',
            label: t('users.email'),
            type: FILTER_TYPES.TEXT,
        },
        {
            fieldName: 'assignRoleDate',
            label: t('users.assignRoleDate'),
            type: FILTER_TYPES.DATE_RANGE,
            apiParamName: 'assign_role_date',
            apiValueFromConverter,
            apiValueToConverter
        },
        {
            fieldName: 'status',
            label: t('users.status'),
            type: FILTER_TYPES.LIST,
            params: userStatusOptions,
            apiParamName: 'statuses',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
    ], [userStatusOptions, apiValueFromConverter, apiValueToConverter,]);

    return (
        <div className='user-profile-container'>
            <ConfirmDialog visible={deleteProfileDialogState.visible}
                onHide={() => setDeleteProfileDialogState({ visible: false, profileId: null, state: null })}
                onConfirm={() => deleteProfile()}
                title={t('users.deleteProfileDialog.title')}
                message={t('users.deleteProfileDialog.message')}
                actionButtonLabel={t('users.deleteProfileDialog.confirmButtonLabel')}
                variant={"danger"}
                delayedConfirm={true}
            />
            <TabView activeIndex={activeTab} onTabChange={e => setActiveTab(e.index)}>
                <TabPanel header={t('userprofile.tabs.general')} key={mainKey}>
                    <div className='user-profile-general-container'>
                        <div className='header'>
                            <InputText label={t('userprofile.name')} id='name' value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                validator={validator}
                                validations='required|max:40|name_unique'
                                onBlur={handleCheckNameStatus}
                            />
                            <InputText label={t('userprofile.description')} id='description' value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                validator={validator}
                                validations='max:200'
                            />
                          
                        </div>
                    </div>
                </TabPanel>
                {viewMode === 'EDIT' && <TabPanel header={t('userprofile.tabs.assignedUsers')}>
                    <DataTable fetchData={fetchAssigedUsersData}
                        filterConfig={assignedUsersFilterConfig}
                        //tableToolbarRight={tableToolbarRight}
                        dataTableContext={UsersAssignedToProfileDataTableContext}
                    
                    >
                        <Column field='userName' header={t('users.userName')} sortable sortField='name' id='name' />
                        <Column field='email' header={t('users.email')} sortable id='email' />
                        <Column field='assignRoleDate' header={t('users.assignRoleDate')} sortable id='assignRoleDate' body={row => RenderedHelper.formatTimestamp(row.assignRoleDate)} />
                        <Column field='status' header={t('users.status')} sortable id='status' body={row => RenderedHelper.formatEnum(row.status, 'UserStatus')} />
                    </DataTable>
                </TabPanel>}
            </TabView>

            {activeTab === TABS.GENERAL && (
                <>
                    <div className='content-panel'>
                <div className='switches-container'>
                    <InputSwitch
                        label={t('userprofile.vendappAccess')}
                        id='vendappAccess'
                        checked={vendappAccess}
                        onChange={e => setVendappAccess(e.value)}
                    />
                    <div className='assigned-services-header'>
                        <InputSwitch
                            label={t('userprofile.assignedServicesTaskTypes')}
                            id='assignedServicesTaskTypes'
                            checked={assignedServicesTaskTypes}
                            onChange={e => {
                                const newValue = e.value;
                                setAssignedServicesTaskTypes(newValue);
                                if (!newValue) {
                                    setSelectedTaskTypes({
                                        PRODUCT_RESTOCKING: false,
                                        CASH_OPERATION: false,
                                        REPLACEMENT: false,
                                        OPERATIONAL: false,
                                        INVENTORY_CHECK: false,
                                        TECHNICAL: false
                                    });
                                }
                            }}
                        />
                        {assignedServicesTaskTypes && (
                            <div className='select-all-task-types'
                                onClick={() => {
                                    const allSelected = Object.values(selectedTaskTypes).every(v => v);
                                    setSelectedTaskTypes({
                                        PRODUCT_RESTOCKING: !allSelected,
                                        CASH_OPERATION: !allSelected,
                                        REPLACEMENT: !allSelected,
                                        OPERATIONAL: !allSelected,
                                        INVENTORY_CHECK: !allSelected,
                                        TECHNICAL: !allSelected
                                    });
                                }}
                            >
                                <SelectAllIcon className='icon' />
                                <span className='label'>{t('userprofile.selectAll')}</span>
                            </div>
                        )}
                    </div>
                </div>

                {assignedServicesTaskTypes && (
                    <div className='task-types-panel'>
                        <div className='task-types-grid'>
                            <div className='task-type-item'>
                                <span className='label-text'>{t('TaskType.PRODUCT_RESTOCKING')}</span>
                                <div className='dots-separator'></div>
                                <Checkbox
                                    inputId='task-type-PRODUCT_RESTOCKING'
                                    checked={selectedTaskTypes.PRODUCT_RESTOCKING}
                                    onChange={e => setSelectedTaskTypes(prev => ({ ...prev, PRODUCT_RESTOCKING: e.checked }))}
                                    disabled={!assignedServicesTaskTypes}
                                    showLabel={false}
                                />
                            </div>
                            <div className='task-type-item'>
                                <span className='label-text'>{t('TaskType.CASH_OPERATION')}</span>
                                <div className='dots-separator'></div>
                                <Checkbox
                                    inputId='task-type-CASH_OPERATION'
                                    checked={selectedTaskTypes.CASH_OPERATION}
                                    onChange={e => setSelectedTaskTypes(prev => ({ ...prev, CASH_OPERATION: e.checked }))}
                                    disabled={!assignedServicesTaskTypes}
                                    showLabel={false}
                                />
                            </div>
                            <div className='task-type-item'>
                                <span className='label-text'>{t('TaskType.REPLACEMENT')}</span>
                                <div className='dots-separator'></div>
                                <Checkbox
                                    inputId='task-type-REPLACEMENT'
                                    checked={selectedTaskTypes.REPLACEMENT}
                                    onChange={e => setSelectedTaskTypes(prev => ({ ...prev, REPLACEMENT: e.checked }))}
                                    disabled={!assignedServicesTaskTypes}
                                    showLabel={false}
                                />
                            </div>
                            <div className='task-type-item'>
                                <span className='label-text'>{t('ManualTaskType.OPERATIONAL')}</span>
                                <div className='dots-separator'></div>
                                <Checkbox
                                    inputId='task-type-OPERATIONAL'
                                    checked={selectedTaskTypes.OPERATIONAL}
                                    onChange={e => setSelectedTaskTypes(prev => ({ ...prev, OPERATIONAL: e.checked }))}
                                    disabled={!assignedServicesTaskTypes}
                                    showLabel={false}
                                />
                            </div>
                            <div className='task-type-item'>
                                <span className='label-text'>{t('TaskType.INVENTORY_CHECK')}</span>
                                <div className='dots-separator'></div>
                                <Checkbox
                                    inputId='task-type-INVENTORY_CHECK'
                                    checked={selectedTaskTypes.INVENTORY_CHECK}
                                    onChange={e => setSelectedTaskTypes(prev => ({ ...prev, INVENTORY_CHECK: e.checked }))}
                                    disabled={!assignedServicesTaskTypes}
                                    showLabel={false}
                                />
                            </div>
                            <div className='task-type-item'>
                                <span className='label-text'>{t('ManualTaskType.TECHNICAL')}</span>
                                <div className='dots-separator'></div>
                                <Checkbox
                                    inputId='task-type-TECHNICAL'
                                    checked={selectedTaskTypes.TECHNICAL}
                                    onChange={e => setSelectedTaskTypes(prev => ({ ...prev, TECHNICAL: e.checked }))}
                                    disabled={!assignedServicesTaskTypes}
                                    showLabel={false}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            <div className='content-panel'>
                <div className='portal-access-header'>
                    <InputSwitch
                        label={t('userprofile.portalAccess')}
                        id='portalAccess'
                        checked={form.portalAccess}
                        onChange={e => {
                            const newValue = e.value;
                            setForm(f => {
                                const updatedForm = { ...f, portalAccess: newValue };

                                // Jeśli wyłączamy portalAccess, wyłączamy wszystkie uprawnienia
                                if (!newValue) {
                                    updatedForm.groups = f.groups?.map(g => ({
                                        ...g,
                                        read: false,
                                        write: false,
                                        tabs: g.tabs?.length > 0 ? setAllChildrenToFalse(g.tabs) : []
                                    })) || [];
                                }

                                return updatedForm;
                            });
                        }}
                    />
                    {form.portalAccess && (
                        <div
                            className='select-all'
                            onClick={() => {
                                handleSelectAll();
                            }}
                        >
                            <SelectAllIcon className='icon' />
                            <span className='label'>{t('userprofile.selectAll')}</span>
                        </div>
                    )}
                </div>
                
                {form.portalAccess && (
                    <>

                        <div className='permissions-container'>
                            <PrimeReactDataTable loading={isTemplateLoading} value={form.groups || []} className="permissions-table" expandedRows={expandedRows} dataKey='code'
                                    rowExpansionTemplate={row => {
                                        const renderNestedTabs = (tabs, level = 0, parentEnabled = true) => {
                                            return tabs.map(tab => {
                                                // Wyłączone jeśli portalAccess = false LUB rodzic jest wyłączony
                                                const isDisabled = !form.portalAccess || !parentEnabled;
                                                const headerClass = `subgroup-header level-${level}${isDisabled ? ' disabled' : ''}`;

                                                return (
                                                    <div key={tab.code} className={`subgroup-item level-${level}`}>
                                                        <div className={headerClass}>
                                                            <div className='permission-name'>{t(`UserPermission.${tab.code}`)}</div>
                                                            <div className='separator' />
                                                            <InputSwitch
                                                                checked={tab.value || false}
                                                                disabled={isDisabled}
                                                                className='subgroup-switch'
                                                                onChange={e => {
                                                                    if (!isDisabled) {
                                                                        handleTabPermissionChange(row.code, tab.code, e.value);
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                        {tab.tabs && tab.tabs.length > 0 && (
                                                            <div className={`nested-permissions level-${level + 1}`}>
                                                                {renderNestedTabs(tab.tabs, level + 1, parentEnabled && (tab.value || false))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            });
                                        };

                                        // Główne taby są włączone jeśli portalAccess = true I grupa ma READ permission
                                        const groupReadEnabled = form.portalAccess && (row.read || false);

                                        return (
                                            <div className='subgroup-container'>
                                                {row.tabs && row.tabs.length > 0 &&
                                                    renderNestedTabs(row.tabs, 0, groupReadEnabled)
                                                }
                                            </div>
                                        )
                                    }}
                                    rowClassName={(rowData) => {
                                        return expandedRows?.[rowData.code] ? 'is-expanded' : '';
                                    }}
                                >
                                    <Column field='code' header={t('userprofile.module')} id='module' sortable={false} body={row => t(`MerchantUserPermissionStructure.${row.code}`)} />
                                    <Column field='revision' header={t('userprofile.revision')} id='revision' sortable={false} body={revisionTemplate} />
                                    <Column field='editing' header={t('userprofile.editing')} id='editing' sortable={false} body={editingTemplate} />
                                </PrimeReactDataTable>
                        </div>
                    </>
                )}
                <div className='flex w-full mt-4 gap-2'>
                    <Button label={t('user.mainInformation.save')} className='ml-auto' onClick={handleSave} loading={isSaving} disabled={isTemplateLoading} />
                    <Button label={t('user.mainInformation.cancel')} severity="secondary" onClick={() => navigate(`/users?${navigationContext.getContextParam()}`)} />
                </div>
            </div>
                </>
            )}

        </div>
    )
}

export default UserProfile;